import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { DynamicIcon } from './icons/DynamicIcon';
import toast from 'react-hot-toast';

interface LowStockIngredient {
  id: string;
  name: string;
  current_stock: number;
  minimum_stock: number;
  low_stock_alert: number;
  unit: string;
  days_since_purchase: number | null;
}

export const InventoryAlert: React.FC = () => {
  const [lowStockItems, setLowStockItems] = useState<LowStockIngredient[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    fetchLowStockIngredients();
  }, []);

  const fetchLowStockIngredients = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('check_low_stock_ingredients');

      if (error) throw error;
      
      setLowStockItems(data || []);
      
      // Show toast notification if there are low stock items
      if (data && data.length > 0) {
        toast.custom(
          (t) => (
            <div
              className={`${
                t.visible ? 'animate-enter' : 'animate-leave'
              } max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}
            >
              <div className="flex-1 w-0 p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    <DynamicIcon name="alertTriangle" size={24} className="text-amber-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      Alerta de Estoque Baixo
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {data.length} {data.length === 1 ? 'ingrediente está' : 'ingredientes estão'} com estoque baixo.
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex border-l border-gray-200">
                <button
                  onClick={() => {
                    setIsOpen(true);
                    toast.dismiss(t.id);
                  }}
                  className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-blue-600 hover:text-blue-500 focus:outline-none"
                >
                  Ver
                </button>
              </div>
            </div>
          ),
          { duration: 5000 }
        );
      }
    } catch (err) {
      console.error('Error fetching low stock ingredients:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStockLevelColor = (current: number, minimum: number) => {
    const ratio = current / minimum;
    if (ratio <= 0.25) return 'text-red-600';
    if (ratio <= 0.5) return 'text-amber-600';
    if (ratio <= 0.75) return 'text-yellow-600';
    return 'text-blue-600';
  };

  if (lowStockItems.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="relative p-2 text-amber-500 hover:text-amber-600 focus:outline-none"
      >
        <DynamicIcon name="alertTriangle" size={20} />
        <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
          {lowStockItems.length}
        </span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <div className="flex items-center">
                <DynamicIcon name="alertTriangle" size={24} className="text-amber-500 mr-3" />
                <h2 className="text-xl font-semibold text-gray-900">Alerta de Estoque Baixo</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <DynamicIcon name="x" size={20} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex flex-col items-center">
                    <DynamicIcon name="loader" size={32} className="animate-spin text-blue-600 mb-4" />
                    <p className="text-gray-600">Carregando dados de estoque...</p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ingrediente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estoque Atual
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estoque Mínimo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Última Compra
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {lowStockItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900">{item.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className={`${getStockLevelColor(item.current_stock, item.minimum_stock)} font-medium`}>
                              {item.current_stock} {item.unit}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-gray-500">
                              {item.minimum_stock} {item.unit}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-gray-500">
                              {item.days_since_purchase !== null 
                                ? `${item.days_since_purchase} dias atrás` 
                                : 'Não registrado'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                              ${item.current_stock <= item.minimum_stock * 0.25 
                                ? 'bg-red-100 text-red-800' 
                                : item.current_stock <= item.minimum_stock * 0.5 
                                  ? 'bg-amber-100 text-amber-800' 
                                  : 'bg-yellow-100 text-yellow-800'}`}>
                              {item.current_stock <= item.minimum_stock * 0.25 
                                ? 'Crítico' 
                                : item.current_stock <= item.minimum_stock * 0.5 
                                  ? 'Muito Baixo' 
                                  : 'Baixo'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center p-6 border-t bg-gray-50">
              <div className="text-sm text-gray-500">
                Recomendação: Faça um pedido de reposição para os itens em estado crítico.
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Fechar
                </button>
                <button
                  onClick={() => {
                    toast.success('Gerando lista de compras...');
                    setIsOpen(false);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Gerar Lista de Compras
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};